/**
 * Genehmigungen — Sammelmappe (Joerg 2026-07-07).
 *
 * Portal-weite Freigabe-Inbox fuer die Geschaeftsfuehrung: aggregiert
 * offene Freigaben aus mehreren Modulen. MVP: Zeiterfassung (Abwesenheits-
 * Antraege + Zeitkorrekturen) + Baustelle (Aufgaben ohne Owner). Rechnungen/
 * Angebote + Fuhrpark-Schaeden sind Platzhalter fuer Ausbaustufe 2.
 *
 * Berichts-Verifizierung gehoert bewusst NICHT rein — das ist Selbst-Aufgabe
 * des Erstellers, kein GF-Vorgang.
 *
 * Datenquelle: GET /api/portal/genehmigungen/inbox
 * Aktions-Endpoints: PUT /api/modules/zeiterfassung/admin/{absences,corrections}/:id
 * Sichtbarkeit: Adressbuch-Gruppe „Geschaeftsfuehrung" oder Admin
 * (Backend-403 → Redirect auf /).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, XCircle, ExternalLink, Loader2, RefreshCw, Clock, Hammer,
  Receipt, Truck, AlertCircle, ClipboardList,
} from 'lucide-react';
import { api } from '@onvice/portal-shell-api';

type UrgencyLevel = 'ok' | 'warn' | 'urgent';
type ChipKind = 'info' | 'warn' | 'urgent';

interface Chip { kind: ChipKind; text: string }

interface ActionSpec {
  path: string;
  method: 'PUT' | 'POST';
  body: Record<string, unknown>;
  needsComment?: boolean;
}

interface Item {
  id: string;
  module: string;
  kind: string;
  primary: string;
  chips: Chip[];
  meta: string;
  context?: string;
  ageLabel: string;
  ageHours: number;
  urgencyLevel: UrgencyLevel;
  openHref: string;
  approve?: ActionSpec;
  reject?: ActionSpec;
}

interface SubCategory { key: string; label: string; count: number; items: Item[] }

interface Section {
  key: string;
  label: string;
  icon: string;
  moduleHref?: string;
  counts: { total: number; urgent: number };
  subs?: SubCategory[];
  placeholder?: boolean;
  placeholderText?: string;
}

interface InboxResponse {
  success: boolean;
  data: {
    counts: { total: number; urgent: number; today: number };
    sections: Section[];
  };
  error?: string;
}

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Clock, Hammer, Receipt, Truck,
};

const CHIP_STYLE: Record<ChipKind, string> = {
  info: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  warn: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  urgent: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const URGENCY_LABEL_STYLE: Record<UrgencyLevel, string> = {
  ok: 'text-slate-400',
  warn: 'text-amber-300',
  urgent: 'text-red-300',
};

type FilterModule = 'alle' | 'zeiterfassung' | 'bauberichte' | 'rechnungen' | 'fuhrpark';
type FilterTime = 'offen' | 'ueberfaellig' | 'heute' | 'woche';

export default function GenehmigungenPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<InboxResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [filterModule, setFilterModule] = useState<FilterModule>('alle');
  const [filterTime, setFilterTime] = useState<FilterTime>('offen');

  const [rejectTarget, setRejectTarget] = useState<{ item: Item } | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      // apiFetch liefert die volle Response {success, data} — nicht nur data.
      const r = await api.get<InboxResponse>('/portal/genehmigungen/inbox');
      if (r.success) setData(r.data);
      else setError(r.error ?? 'Sammelmappe konnte nicht geladen werden.');
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 403) {
        navigate('/', { replace: true });
        return;
      }
      setError((err as Error).message ?? 'Fehler beim Laden');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(true), 30_000);
    return () => window.clearInterval(t);
  }, [load, reloadTick]);

  async function runAction(item: Item, spec: ActionSpec): Promise<void> {
    setBusy(`${item.id}:${spec.body['decision']}`);
    try {
      // spec.path beginnt mit /modules/… — api.put/post prefixt selbst /api,
      // deshalb hier KEIN manuelles /api davor (Doppel-Praefix /api/api/ → 404).
      // STAT-P7 (2026-08-07): Bericht-Approve nutzt POST, Absences/Corrections PUT.
      const call = spec.method === 'POST' ? api.post : api.put;
      await call(spec.path, spec.body);
      // Silent-Reload, damit die Zeile aus der Sammelmappe verschwindet.
      setReloadTick((t) => t + 1);
    } catch (err) {
      setError(`Aktion fehlgeschlagen: ${(err as Error).message ?? 'unbekannt'}`);
    } finally {
      setBusy(null);
    }
  }

  function openReject(item: Item): void {
    setRejectTarget({ item });
    setRejectComment('');
  }

  async function confirmReject(): Promise<void> {
    if (!rejectTarget || rejectBusy) return;
    const comment = rejectComment.trim();
    if (!comment) return;
    const spec = rejectTarget.item.reject;
    if (!spec) return;
    setRejectBusy(true);
    try {
      // Kein manuelles /api davor (siehe runAction). Method aus spec respektieren.
      const call = spec.method === 'POST' ? api.post : api.put;
      await call(spec.path, { ...spec.body, comment });
      setRejectTarget(null);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setError(`Ablehnen fehlgeschlagen: ${(err as Error).message ?? 'unbekannt'}`);
    } finally {
      setRejectBusy(false);
    }
  }

  const filteredSections = useMemo(() => {
    if (!data) return [];
    return data.sections
      .filter((s) => filterModule === 'alle' || s.key === filterModule)
      .map((s) => {
        if (!s.subs) return s;
        const subs = s.subs.map((sub) => ({
          ...sub,
          items: sub.items.filter((it) => {
            if (filterTime === 'offen') return true;
            if (filterTime === 'ueberfaellig') return it.urgencyLevel === 'urgent';
            if (filterTime === 'heute') return it.ageHours < 24 && it.ageHours > 0;
            if (filterTime === 'woche') return it.ageHours <= 24 * 7;
            return true;
          }),
        }));
        return { ...s, subs };
      });
  }, [data, filterModule, filterTime]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error ?? 'Sammelmappe konnte nicht geladen werden.'}
        </div>
      </div>
    );
  }

  const c = data.counts;

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      {/* Header + Stat-Chips */}
      <header className="space-y-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
              <ClipboardList size={22} className="text-brand-300" /> Genehmigungen
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Alle offenen Freigaben aus allen Modulen. Reihenfolge: ältester zuerst. Aktualisiert sich alle 30 s.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-800 text-slate-300 hover:bg-slate-800 text-sm disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aktualisieren
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCard label="Offen gesamt" value={c.total} />
          <StatCard label="Überfällig >3 Tage" value={c.urgent} tone={c.urgent > 0 ? 'red' : 'neutral'} />
          <StatCard label="Heute eingegangen" value={c.today} tone="sky" />
        </div>
      </header>

      {/* Filter */}
      <section className="flex items-center gap-2 flex-wrap py-1 text-xs">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Zeitraum</span>
        <FilterChip active={filterTime === 'offen'} onClick={() => setFilterTime('offen')}>Alles offen ({c.total})</FilterChip>
        <FilterChip active={filterTime === 'ueberfaellig'} onClick={() => setFilterTime('ueberfaellig')}>Überfällig ({c.urgent})</FilterChip>
        <FilterChip active={filterTime === 'heute'} onClick={() => setFilterTime('heute')}>Heute ({c.today})</FilterChip>
        <FilterChip active={filterTime === 'woche'} onClick={() => setFilterTime('woche')}>Diese Woche</FilterChip>

        <span className="text-[10px] uppercase tracking-wider text-slate-500 mx-2 ml-4">Modul</span>
        <FilterChip active={filterModule === 'alle'} onClick={() => setFilterModule('alle')}>Alle</FilterChip>
        {data.sections.map((s) => (
          <FilterChip
            key={s.key}
            active={filterModule === (s.key as FilterModule)}
            onClick={() => setFilterModule(s.key as FilterModule)}
          >
            {s.label} ({s.counts.total})
          </FilterChip>
        ))}
      </section>

      {error && (
        <div className="p-3 rounded-lg bg-red-950/40 border border-red-900/50 text-red-300 text-sm">{error}</div>
      )}

      {/* Sektionen */}
      {filteredSections.map((s) => (
        <SectionCard key={s.key} section={s} busy={busy} onApprove={runAction} onReject={openReject} />
      ))}

      {/* Ablehn-Modal */}
      {rejectTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !rejectBusy && setRejectTarget(null)}
        >
          <div
            className="bg-slate-900 border border-slate-800 rounded-xl max-w-md w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-slate-100">Ablehnen — {rejectTarget.item.primary}</h3>
            <p className="text-xs text-slate-400">Bitte gib einen Kommentar mit — der Antragsteller sieht ihn als Begründung.</p>
            <textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={4}
              placeholder="Begründung…"
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
              autoFocus
            />
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setRejectTarget(null)}
                disabled={rejectBusy}
                className="px-3 py-1.5 rounded-lg text-sm text-slate-400 hover:bg-slate-800"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => void confirmReject()}
                disabled={rejectBusy || !rejectComment.trim()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/40 disabled:opacity-60"
              >
                {rejectBusy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />} Ablehnen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = 'neutral' }: { label: string; value: number; tone?: 'neutral' | 'red' | 'sky' }) {
  const color = tone === 'red' ? 'text-red-300' : tone === 'sky' ? 'text-sky-300' : 'text-slate-100';
  return (
    <div className="p-3 rounded-lg bg-slate-900/50 border border-slate-800">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold tabular-nums leading-tight ${color}`}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? 'bg-blue-500/20 text-blue-200 border-blue-500/50'
          : 'bg-slate-900/60 text-slate-400 border-slate-800 hover:border-slate-600'
      }`}
    >
      {children}
    </button>
  );
}

function SectionCard({
  section, busy, onApprove, onReject,
}: {
  section: Section;
  busy: string | null;
  onApprove: (item: Item, spec: ActionSpec) => void;
  onReject: (item: Item) => void;
}) {
  const Icon = ICON_MAP[section.icon] ?? AlertCircle;
  const empty = !section.placeholder && (!section.subs || section.subs.every((sub) => sub.items.length === 0));
  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/60 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-slate-300" />
          <h2 className="font-semibold text-slate-100">{section.label}</h2>
          <span className="pill-count">{section.counts.total} offen</span>
          {section.counts.urgent > 0 && (
            <span className="pill-count-urgent">{section.counts.urgent} überfällig</span>
          )}
        </div>
        {section.moduleHref && (
          <Link to={section.moduleHref} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
            Modul öffnen <ExternalLink size={12} />
          </Link>
        )}
      </div>

      {section.placeholder ? (
        <div className="px-4 py-6 text-sm text-slate-500 italic">{section.placeholderText}</div>
      ) : empty ? (
        <div className="px-4 py-6 text-sm text-slate-500 italic">Nichts offen.</div>
      ) : (
        section.subs?.map((sub) => (
          sub.items.length === 0 ? null : (
            <div key={sub.key}>
              <div className="px-4 py-2 bg-slate-900/40 border-b border-slate-800/60 flex items-center gap-2">
                <span className="text-xs text-slate-500 uppercase tracking-wider">{sub.label}</span>
                <span className="pill-count">{sub.items.length}</span>
              </div>
              <ul className="divide-y divide-slate-800/40">
                {sub.items.map((it) => (
                  <ItemRow key={it.id} item={it} busy={busy} onApprove={onApprove} onReject={onReject} />
                ))}
              </ul>
            </div>
          )
        ))
      )}

      <style>{`
        .pill-count { display: inline-flex; align-items: center; padding: 0.05rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; background: rgb(71 85 105 / 0.3); color: rgb(203 213 225); }
        .pill-count-urgent { display: inline-flex; align-items: center; padding: 0.05rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; background: rgb(239 68 68 / 0.15); color: rgb(252 165 165); border: 1px solid rgb(239 68 68 / 0.4); }
      `}</style>
    </section>
  );
}

function ItemRow({
  item, busy, onApprove, onReject,
}: {
  item: Item;
  busy: string | null;
  onApprove: (item: Item, spec: ActionSpec) => void;
  onReject: (item: Item) => void;
}) {
  const approveBusy = busy === `${item.id}:genehmigt`;
  return (
    <li className="px-4 py-2.5 flex items-start gap-3 hover:bg-slate-900/40 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-100 text-sm truncate">{item.primary}</span>
          {item.chips.map((c, i) => (
            <span key={i} className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[11px] ${CHIP_STYLE[c.kind]}`}>
              {c.text}
            </span>
          ))}
        </div>
        <div className="text-xs text-slate-400 mt-0.5 truncate">{item.meta}</div>
        {item.context && <div className="text-xs text-slate-500 mt-0.5 truncate">„{item.context}"</div>}
      </div>
      <div className={`text-xs whitespace-nowrap self-center ${URGENCY_LABEL_STYLE[item.urgencyLevel]}`}>
        {item.ageLabel}
        {item.urgencyLevel === 'urgent' && ' ⚠'}
      </div>
      <div className="flex items-center gap-1 self-center">
        {item.approve && (
          <button
            type="button"
            onClick={() => onApprove(item, item.approve!)}
            disabled={approveBusy}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30 disabled:opacity-50"
            title="Genehmigen"
          >
            {approveBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Genehmigen
          </button>
        )}
        {item.reject && (
          <button
            type="button"
            onClick={() => onReject(item)}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-500/15 text-red-300 border border-red-500/30 hover:bg-red-500/30"
            title="Ablehnen (Kommentar nötig)"
          >
            <XCircle size={12} /> Ablehnen
          </button>
        )}
        <Link
          to={item.openHref}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700"
          title="Detail öffnen"
        >
          <ExternalLink size={12} /> Öffnen
        </Link>
      </div>
    </li>
  );
}
